'use client'

import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'


import { fetchAssessmentSolutions } from '../../actions'

// UPDATED: Enhanced Fullscreen Assessment Container that covers entire viewport
const FullscreenAssessmentContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  display: flex;
  background: #f9fafb;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
`

const SidebarNav = styled.div`
  width: ${props => (props.isExpanded ? '25%' : '60px')};
  min-width: ${props => (props.isExpanded ? '300px' : '60px')};
  max-width: ${props => (props.isExpanded ? '25%' : '60px')};
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;
  overflow: hidden;
`

const SidebarHeader = styled.div`
  padding: ${props => (props.isExpanded ? '20px 16px' : '20px 0')};
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.isExpanded ? 'space-between' : 'center')};
  background: #f8fafc;
  min-height: 64px;
  flex-shrink: 0;
`

const SidebarTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #374151;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
`

const ToggleButton = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => (props.isExpanded ? '16px 12px' : '16px 6px')};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const QuestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => (props.isExpanded ? '10px 8px' : '12px 6px')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  min-height: ${props => (props.isExpanded ? '40px' : '48px')};
  position: relative;
  margin-bottom: 2px;

  &:hover {
    background: #f8fafc;
    border-color: #d1d5db;
    transform: translateX(2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  ${props =>
    props.isExpanded
      ? ''
      : `
    justify-content: center;
  `}
`

// UPDATED: Simplified question number styling with subtle colors
const QuestionNumber = styled.div`
  /* Base sizing - larger when sidebar is collapsed for better touch targets */
  width: ${props => (props.isExpanded ? '28px' : '36px')};
  height: ${props => (props.isExpanded ? '28px' : '36px')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${props => (props.isExpanded ? '12px' : '13px')};
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease;
  border: 2px solid;

  /* Status-based styling for different question states */
  ${props => {
    switch (props.status) {
      case 'current':
        // Currently viewing this question - blue theme with glow effect
        return `
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        `
      case 'correct':
        // Question answered correctly - green theme
        return `
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          border-color: #10b981;
        `
      case 'incorrect':
        // Question answered incorrectly - red theme
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border-color: #ef4444;
        `
      case 'unanswered':
        // Question not attempted - gray theme
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #6b7280;
          border-color: #9ca3af;
        `
      default:
        // Default state - neutral gray
        return `
          background: #f9fafb;
          color: #6b7280;
          border-color: #e5e7eb;
        `
    }
  }}
`

const QuestionText = styled.div`
  margin-left: ${props => (props.isExpanded ? '8px' : '0')};
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  flex: 1;
  line-height: 1.3;
  display: -webkit-box; // <-- Use flexbox for ellipsis
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; // <-- Clamp to 2 lines
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 28px;
  & > p {
    margin: 0 !important;
    padding: 0 !important;
  }
  ${props =>
    props.isExpanded &&
    `
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
  `}
  ${props =>
    !props.isExpanded &&
    `
    display: none;
  `}
  & .katex {
    font-size: 1em !important;
  }
  & .katex-html {
    color: #4b5563 !important;
  }
`

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const AssessmentContent = styled.div`
  flex: 1;
  padding: 20px 24px 100px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`

const AssessmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`

const AssessmentTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #eff6ff;
  }
`

const AssessmentSubtitle = styled.h2`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px 0;
  font-weight: 500;
`

// UPDATED: Summary section with space-between layout
const SummarySection = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

// UPDATED: Group for the three summary items on the right
const SummaryItemsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 60px;
`

// UPDATED: Simplified summary number colors
const SummaryNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.color || '#3b82f6'};
  margin-bottom: 4px;
`

const SummaryLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ScoreContainer = styled.div`
  text-align: center;
  min-width: 120px;
`

const ScoreText = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
`

const PercentageText = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #3b82f6;
`

// UPDATED: Fixed layout with proper positioning for badge and timer
const QuestionContainer = styled.div`
  background: #ffffff;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  position: relative;
`

// UPDATED: Fixed difficulty badge positioning
const DifficultyBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 12px;

  ${props => {
    switch (props.difficulty) {
      case 'easy':
        return `
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #bbf7d0;
        `
      case 'medium':
        return `
          background: #fffbeb;
          color: #d97706;
          border: 1px solid #fde68a;
        `
      case 'hard':
        return `
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        `
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        `
    }
  }}
`

const QuestionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
`

// UPDATED: Fixed question header layout with proper spacing
const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const QuestionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`

// UPDATED: Simplified status icon
const StatusIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;

  ${props => {
    if (props.isCorrect) {
      return `
        background: #10b981;
        color: white;
      `
    } else {
      return `
        background: #ef4444;
        color: white;
      `
    }
  }}
`

const QuestionLabel = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`

// UPDATED: Simplified time taken badge
const TimeTakenBadge = styled.span`
  background: #f0fdf4;
  color: #15803d;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #bbf7d0;
`

// UPDATED: Enhanced question text container with Markdown styling support
const QuestionTextContent = styled.div`
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
  font-weight: 500;
  margin-bottom: 20px;
  background: #f8fafc;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;

  * {
    color: #374151 !important; // Force all child elements to use this color
  }

  /* Markdown styling */
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 16px 0 8px 0;
    font-weight: 600;
    color: #1f2937;
  }

  & h1 {
    font-size: 1.5em;
  }
  & h2 {
    font-size: 1.3em;
  }
  & h3 {
    font-size: 1.2em;
  }
  & h4 {
    font-size: 1.1em;
  }
  & h5 {
    font-size: 1em;
  }
  & h6 {
    font-size: 0.95em;
  }

  & p {
    margin: 8px 0;
    line-height: 1.6;
  }

  & strong {
    font-weight: 700;
    color: #1f2937;
  }

  & em {
    font-style: italic;
  }

  & ul,
  & ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  & li {
    margin: 4px 0;
  }

  & code {
    background: #f1f5f9;
    color: #374151;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 0.9em;
    font-family: 'Courier New', monospace;
  }

  & pre {
    background: #f1f5f9;
    color: #374151;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 12px 0;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }

  & blockquote {
    border-left: 4px solid #d1d5db;
    padding-left: 12px;
    margin: 12px 0;
    color: #6b7280;
    font-style: italic;
  }

  /* LaTeX math styling */
  & .katex {
    font-size: 1em !important;
  }

  & .katex-display {
    margin: 16px 0 !important;
    text-align: center;
  }

  & .katex-html {
    color: #1f2937 !important;
  }
`

const OptionsSection = styled.div`
  margin-bottom: 20px;
`

// UPDATED: Match assessment.js header styling
const OptionsSectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: #16a34a;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 4px;
`

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

// UPDATED: Simplified option item styling
const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 6px;
  border: 1px solid;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #374151;
  min-height: 50px;
  gap: 12px;

  ${props => {
    if (props.isCorrect) {
      return `
        background: #f0fdf4;
        border-color: #10b981;
        color: #15803d;
      `
    } else if (props.isUserAnswer) {
      return `
        background: #fef2f2;
        border-color: #ef4444;
        color: #dc2626;
      `
    } else {
      return `
        background: #ffffff;
        border-color: #e5e7eb;
        color: #374151;
      `
    }
  }}
`

// UPDATED: Fixed option icon with perfect centering
const OptionIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;

  ${props => {
    if (props.isCorrect) {
      return `
        background: #10b981;
        color: white;
      `
    } else if (props.isUserAnswer) {
      return `
        background: #ef4444;
        color: white;
      `
    } else {
      return `
        background: #f3f4f6;
        color: #6b7280;
        border: 1px solid #e5e7eb;
      `
    }
  }}
`

// UPDATED: Enhanced option text with proper alignment
const OptionText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  flex: 1;
  display: flex;
  align-items: center;

  /* Markdown styling for options */
  & strong {
    font-weight: 700;
  }

  & em {
    font-style: italic;
  }

  & code {
    background: rgba(156, 163, 175, 0.1);
    padding: 2px 4px;
    border-radius: 2px;
    font-size: 0.9em;
    font-family: 'Courier New', monospace;
  }

  & p {
    margin: 0;
    line-height: 1.5;
  }

  /* LaTeX math styling for options */
  & .katex {
    font-size: 0.95em !important;
  }

  & .katex-html {
    color: inherit !important;
  }

  & .katex-display {
    margin: 8px 0 !important;
    text-align: left;
  }
`

const OptionLabel = styled.span`
  margin-left: auto;
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  ${props => {
    if (props.isCorrect) {
      return `
        background: #15803d;
        color: white;
      `
    } else if (props.isUserAnswer) {
      return `
        background: #dc2626;
        color: white;
      `
    }
  }}

  // If both isCorrect and isUserAnswer, use green style
  ${props =>
    props.isCorrect &&
    props.isUserAnswer &&
    `
      background: #15803d !important;
      color: white !important;
    `}
`

// UPDATED: Enhanced explanation section with Markdown styling
const ExplanationSection = styled.div`
  background: #fffbeb;
  border: 1px solid #d97706;
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
`

const ExplanationTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`

// UPDATED: Enhanced explanation text with Markdown support and automatic keyword bolding
const ExplanationText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #92400e;
  margin: 0;

  /* Markdown styling for explanations */
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 12px 0 6px 0;
    font-weight: 600;
    color: #92400e;
  }

  & h1 {
    font-size: 1.3em;
  }
  & h2 {
    font-size: 1.2em;
  }
  & h3 {
    font-size: 1.1em;
  }
  & h4 {
    font-size: 1em;
  }
  & h5 {
    font-size: 0.95em;
  }
  & h6 {
    font-size: 0.9em;
  }

  & p {
    margin: 6px 0;
    line-height: 1.6;
  }

  & strong {
    font-weight: 700;
    color: #78350f;
  }

  & em {
    font-style: italic;
  }

  & ul,
  & ol {
    margin: 6px 0;
    padding-left: 16px;
  }

  & li {
    margin: 3px 0;
  }

  & code {
    background: rgba(217, 119, 6, 0.1);
    color: #78350f;
    padding: 1px 3px;
    border-radius: 2px;
    font-size: 0.9em;
    font-family: 'Courier New', monospace;
  }

  & pre {
    background: rgba(217, 119, 6, 0.1);
    color: #78350f;
    padding: 8px;
    border-radius: 3px;
    overflow-x: auto;
    margin: 8px 0;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }

  & blockquote {
    border-left: 3px solid #d97706;
    padding-left: 8px;
    margin: 8px 0;
    color: #a16207;
    font-style: italic;
  }

  /* LaTeX math styling for explanations */
  & .katex {
    font-size: 1em !important;
  }

  & .katex-display {
    margin: 12px 0 !important;
    text-align: center;
  }

  & .katex-html {
    color: #92400e !important;
  }

  /* Custom styling for auto-bolded keywords */
  & .explanation-keyword {
    font-weight: 700;
    color: #78350f;
    background: rgba(217, 119, 6, 0.1);
    padding: 1px 3px;
    border-radius: 2px;
  }
`

// UPDATED: Match assessment.js navigation styling
const NavigationContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: ${props => (props.sidebarExpanded ? '25%' : '60px')};
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 12px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 50;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
`

// UPDATED: Match assessment.js button styling
const NavButton = styled.button`
  background: ${props => (props.disabled ? '#d1d5db' : '#3b82f6')};
  color: ${props => (props.disabled ? '#9ca3af' : 'white')};
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.disabled ? '#d1d5db' : '#2563eb')};
  }
`

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(249, 250, 251, 0.95);
  backdrop-filter: blur(4px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 40px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  max-width: 400px;
  width: 90%;
  text-align: center;
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const LoadingTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
  line-height: 1.3;
`

const LoadingText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  max-width: 280px;
`

const LoadingIcon = styled.div`
  width: 24px;
  height: 24px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;

  svg {
    width: 100%;
    height: 100%;
  }
`

const QuestionPopover = styled.div`
  position: fixed;
  left: ${props => (props.sidebarExpanded ? '25%' : '60px')};
  top: ${props => props.top}px;
  transform: translateY(-50%);
  background: #ffffff;
  color: #374151;
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 350px;
  min-width: 280px;
  z-index: 2000;
  opacity: ${props => (props.show ? 1 : 0)};
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-left: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: #ffffff;
  }

  &::after {
    content: '';
    position: absolute;
    left: -7px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: #e5e7eb;
    z-index: -1;
  }

  .popover-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
  }

  .popover-question-number {
    font-weight: 700;
    color: #3b82f6;
    font-size: 14px;
  }

  .popover-status {
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 12px;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .popover-text {
    line-height: 1.5;
    color: #374151;
    font-weight: 500;
  }
`

const Solutions = ({ assessmentData, onBack }) => {
  const dispatch = useDispatch()
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [solutions, setSolutions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hoveredQuestion, setHoveredQuestion] = useState(null)
  const [popoverPosition, setPopoverPosition] = useState({ top: 0 })
  const hoverTimeoutRef = useRef(null)

  // ADD: refs for sidebar question items
  const questionItemRefs = useRef([])

  const currentAssessment = useSelector(
    state => state.assessment.currentAssessment
  )

  // UPDATED: Ensure consistent fullscreen behavior for solutions view
  useEffect(() => {
    // Maintain fullscreen mode for solutions
    document.body.classList.add('assessment-fullscreen-mode')
    const navbar = document.querySelector(
      'nav, .navbar, .header, .app-header, .top-nav'
    )
    if (navbar) {
      navbar.style.display = 'none'
    }

    return () => {
      // Clean up when solutions view unmounts
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }
  }, [])

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        setLoading(true)
        const assessmentAssignedId =
          currentAssessment?.assessment_assigned_id ||
          assessmentData?.assessment_assigned_id

        if (!assessmentAssignedId) {
          throw new Error('Assessment assigned ID not found')
        }

        const response = await dispatch(
          fetchAssessmentSolutions(assessmentAssignedId)
        )
        setSolutions(response)
      } catch (err) {
        console.error('Error fetching solutions:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchSolutions()
  }, [dispatch, currentAssessment, assessmentData])

  // ADD: Scroll sidebar to current question when index or sidebarExpanded changes
  useEffect(() => {
    if (
      questionItemRefs.current[currentQuestionIndex] &&
      typeof questionItemRefs.current[currentQuestionIndex].scrollIntoView === 'function'
    ) {
      questionItemRefs.current[currentQuestionIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [currentQuestionIndex, sidebarExpanded])

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded)
  }

  const handleQuestionSelect = index => {
    setCurrentQuestionIndex(index)
  }

  const handleQuestionHover = (index, event) => {
    if (sidebarExpanded) return // Only show popover when collapsed

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    const rect = event.currentTarget.getBoundingClientRect()
    setPopoverPosition({ top: rect.top + rect.height / 2 })

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredQuestion(index)
    }, 200)
  }

  const handleQuestionLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    setTimeout(() => {
      setHoveredQuestion(null)
    }, 100)
  }

  const handleNext = () => {
    if (currentQuestionIndex < solutions.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const formatTime = seconds => {
    if (seconds === 0) return '< 1s'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`
    }
    return `${remainingSeconds}s`
  }

  // UPDATED: Helper function to strip HTML/Markdown for popover text
  const stripMarkdown = text => {
    if (!text) return ''
    // Remove markdown syntax for plain text display in popover
    return text
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/\$\$(.*?)\$\$/g, '[Math Expression]') // Replace block math
      .replace(/\$(.*?)\$/g, '[Math]') // Replace inline math
      .trim()
  }

  // UPDATED: Helper function to process explanation text and bold keywords
  // const processExplanationText = (text) => {
  //   if (!text) return ""

  //   // Define keywords to automatically bold (case-insensitive)
  //   const keywords = [
  //     'step 1', 'step1', 'step 2', 'step2', 'step 3', 'step3', 'step 4', 'step4', 'step 5', 'step5',
  //     'step 6', 'step6', 'step 7', 'step7', 'step 8', 'step8', 'step 9', 'step9', 'step 10', 'step10',
  //     'final answer', 'final step', 'conclusion', 'solution', 'answer', 'result',
  //     'first step', 'second step', 'third step', 'fourth step', 'fifth step',
  //     'next step', 'last step', 'therefore', 'hence', 'thus'
  //   ]

  //   let processedText = text

  //   // Process each keyword
  //   keywords.forEach(keyword => {
  //     const regex = new RegExp(`\\b${keyword.replace(/\s+/g, '\\s+')}\\b`, 'gi')
  //     processedText = processedText.replace(regex, `**${keyword}**`)
  //   })

  //   return processedText
  // }

  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingContainer>
          {/* <LoadingIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V11H13V7H7V19H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </LoadingIcon> */}
          <LoadingSpinner />
          <LoadingTitle>Loading Solutions</LoadingTitle>
          <LoadingText>
            Please wait while we fetch your assessment results and prepare
            detailed explanations
          </LoadingText>
        </LoadingContainer>
      </LoadingOverlay>
    )
  }

  if (error) {
    return (
      <LoadingContainer>
        <div style={{ color: '#dc2626' }}>Error loading solutions: {error}</div>
        <BackButton onClick={onBack} style={{ marginTop: '16px' }}>
          Back to Results
        </BackButton>
      </LoadingContainer>
    )
  }

  if (!solutions || !solutions.questions || solutions.questions.length === 0) {
    return (
      <LoadingContainer>
        <div>No solutions available</div>
        <BackButton onClick={onBack} style={{ marginTop: '16px' }}>
          Back to Results
        </BackButton>
      </LoadingContainer>
    )
  }

  const currentQuestion = solutions.questions[currentQuestionIndex]
  const summary = solutions.summary

  // Calculate percentage
  const percentage = Math.max(
    0,
    Math.round((summary.gained_marks / summary.total_marks) * 100)
  )

  return (
    <FullscreenAssessmentContainer>
      <SidebarNav isExpanded={sidebarExpanded}>
        <SidebarHeader isExpanded={sidebarExpanded}>
          {sidebarExpanded && (
            <SidebarTitle isExpanded={sidebarExpanded}>
              Solutions ({solutions?.questions?.length || 0})
            </SidebarTitle>
          )}
          <ToggleButton onClick={toggleSidebar} isExpanded={sidebarExpanded}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={sidebarExpanded ? 'M18 6L6 18M6 6l12 12' : 'M9 18l6-6-6-6'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ToggleButton>
        </SidebarHeader>

        <SidebarContent isExpanded={sidebarExpanded}>
          <QuestionList>
            {solutions.questions.map((question, index) => {
              let status = 'unanswered'
              if (index === currentQuestionIndex) {
                status = 'current'
              } else if (question.userAnswer) {
                status = question.userAnswer.isCorrect ? 'correct' : 'incorrect'
              }

              return (
                <QuestionItem
                  ref={el => (questionItemRefs.current[index] = el)}
                  key={index}
                  isExpanded={sidebarExpanded}
                  onClick={() => handleQuestionSelect(index)}
                  onMouseEnter={e => handleQuestionHover(index, e)}
                  onMouseLeave={handleQuestionLeave}
                >
                  <QuestionNumber status={status} isExpanded={sidebarExpanded}>
                    {index + 1}
                  </QuestionNumber>
                  {sidebarExpanded && (
                    <>
                      <QuestionText isExpanded={sidebarExpanded}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm,remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                          skipHtml={true}
                        >
                          {question.questionText}
                        </ReactMarkdown>
                      </QuestionText>
                    </>
                  )}
                </QuestionItem>
              )
            })}
          </QuestionList>
        </SidebarContent>
      </SidebarNav>

      {hoveredQuestion !== null && !sidebarExpanded && (
        <QuestionPopover
          show={true}
          sidebarExpanded={sidebarExpanded}
          top={popoverPosition.top}
        >
          <div className="popover-header">
            <span className="popover-question-number">
              Question {hoveredQuestion + 1}
            </span>
            <span
              className="popover-status"
              style={{
                background: (() => {
                  const question = solutions.questions[hoveredQuestion]
                  let status = 'unanswered'
                  if (hoveredQuestion === currentQuestionIndex) {
                    status = 'current'
                  } else if (question?.userAnswer) {
                    status = question.userAnswer.isCorrect
                      ? 'correct'
                      : 'incorrect'
                  }
                  switch (status) {
                    case 'current':
                      return '#3b82f6'
                    case 'correct':
                      return '#10b981'
                    case 'incorrect':
                      return '#ef4444'
                    default:
                      return '#9ca3af'
                  }
                })(),
              }}
            >
              {(() => {
                const question = solutions.questions[hoveredQuestion]
                let status = 'unanswered'
                if (hoveredQuestion === currentQuestionIndex) {
                  status = 'current'
                } else if (question?.userAnswer) {
                  status = question.userAnswer.isCorrect
                    ? 'correct'
                    : 'incorrect'
                }
                switch (status) {
                  case 'current':
                    return 'Current'
                  case 'correct':
                    return 'Correct'
                  case 'incorrect':
                    return 'Incorrect'
                  default:
                    return 'Unanswered'
                }
              })()}
            </span>
          </div>
          <div className="popover-text">
            <ReactMarkdown
              remarkPlugins={[remarkGfm,remarkMath]}
              rehypePlugins={[rehypeKatex]}
              skipHtml={true}
            >
              {solutions.questions[hoveredQuestion]?.questionText}
            </ReactMarkdown>
          </div>
        </QuestionPopover>
      )}

      <ContentArea>
        <AssessmentContent>
          <AssessmentHeader>
            <AssessmentTitle>Assessment Solutions</AssessmentTitle>
            <BackButton onClick={onBack}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to Results
            </BackButton>
          </AssessmentHeader>

          {/* <AssessmentSubtitle>
            Question {currentQuestionIndex + 1} of {solutions.questions.length}
          </AssessmentSubtitle> */}

          <SummarySection>
            <ScoreContainer>
              <ScoreText>Your Score</ScoreText>
              <PercentageText>{percentage}%</PercentageText>
            </ScoreContainer>

            <SummaryItemsGroup>
              <SummaryItem>
                <SummaryNumber color="#10b981">
                  {summary.correctAnswers}
                </SummaryNumber>
                <SummaryLabel>Correct</SummaryLabel>
              </SummaryItem>

              <SummaryItem>
                <SummaryNumber color="#ef4444">
                  {summary.incorrectAnswers}
                </SummaryNumber>
                <SummaryLabel>Incorrect</SummaryLabel>
              </SummaryItem>

              <SummaryItem>
                <SummaryNumber color="#9ca3af">
                  {summary.unanswered}
                </SummaryNumber>
                <SummaryLabel>Unanswered</SummaryLabel>
              </SummaryItem>
            </SummaryItemsGroup>
          </SummarySection>

          <QuestionContainer>
            <QuestionHeader>
              <QuestionHeaderLeft>
                <QuestionLabel>
                  Question {currentQuestionIndex + 1}
                </QuestionLabel>
                <DifficultyBadge difficulty={currentQuestion.difficulty}>
                  {currentQuestion.difficulty}
                </DifficultyBadge>
              </QuestionHeaderLeft>
              <TimeTakenBadge>
                Time Taken: {formatTime(currentQuestion.timeTaken)}
              </TimeTakenBadge>
            </QuestionHeader>

            <QuestionLayout>
              <QuestionTextContent>
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  skipHtml={true}
                >
                  {currentQuestion.questionText}
                </ReactMarkdown>
              </QuestionTextContent>

              <OptionsSection>
                <OptionsSectionTitle>Answer Options</OptionsSectionTitle>
                <OptionsList>
                  {currentQuestion.options.map(option => {
                    const isCorrect =
                      option.id === currentQuestion.correctAnswer.id
                    const isUserAnswer =
                      option.id === currentQuestion.userAnswer?.id

                    return (
                      <OptionItem
                        key={option.id}
                        isCorrect={isCorrect}
                        isUserAnswer={isUserAnswer}
                      >
                        <OptionIcon
                          isCorrect={isCorrect}
                          isUserAnswer={isUserAnswer}
                        >
                          {String.fromCharCode(
                            65 + currentQuestion.options.indexOf(option)
                          )}
                        </OptionIcon>
                        <OptionText>
                          <ReactMarkdown
                            remarkPlugins={[remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            skipHtml={true}
                          >
                            {option.text}
                          </ReactMarkdown>
                        </OptionText>
                        {isUserAnswer && (
                          <OptionLabel isUserAnswer isCorrect={isCorrect}>
                            Your Answer
                          </OptionLabel>
                        )}
                        {isCorrect && (
                          <OptionLabel isCorrect>Correct</OptionLabel>
                        )}
                      </OptionItem>
                    )
                  })}
                </OptionsList>
              </OptionsSection>

              {currentQuestion.explanation && (
                <ExplanationSection>
                  <ExplanationTitle>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 17h.01"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Explanation
                  </ExplanationTitle>
                  <ExplanationText>
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      skipHtml={true}
                    >
                      {currentQuestion.explanation}
                    </ReactMarkdown>
                  </ExplanationText>
                </ExplanationSection>
              )}
            </QuestionLayout>
          </QuestionContainer>
        </AssessmentContent>

        <NavigationContainer sidebarExpanded={sidebarExpanded}>
          <NavButton
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </NavButton>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            {currentQuestionIndex + 1} of {solutions.questions.length}
          </div>
          <NavButton
            onClick={handleNext}
            disabled={currentQuestionIndex === solutions.questions.length - 1}
          >
            Next
          </NavButton>
        </NavigationContainer>
      </ContentArea>
    </FullscreenAssessmentContainer>
  )
}

export default Solutions
